import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    Command as CommandIcon,
    BookOpen,
    Repeat,
    MessageCircle,
    Languages,
    Zap,
    Sparkles,
    LayoutDashboard,
    Turtle,
    Mic,
    Compass,
    Search,
    HelpCircle,
} from "lucide-react";

/**
 * Commands: single source of truth used by CommandPalette (global ⌘K) and
 * SlashMenu (inline "/" in the chat input). Kept here so both stay in sync.
 */
export const COMMANDS = [
    { key: "/start", label: "Commencer / Onboarding", intent: "start", icon: Compass, testid: "cmd-start" },
    { key: "/plan", label: "Voir mon parcours", intent: "plan", icon: BookOpen, testid: "cmd-plan" },
    { key: "/lesson", label: "Leçon du jour", intent: "lesson", icon: BookOpen, testid: "cmd-lesson" },
    { key: "/review", label: "Réviser mes mots", intent: "review", icon: Repeat, testid: "cmd-review" },
    { key: "/speak", label: "Conversation IA", intent: "conversation", icon: MessageCircle, testid: "cmd-speak" },
    { key: "/roleplay", label: "Choisir un scénario de jeu de rôle", intent: "conversation", icon: MessageCircle, testid: "cmd-roleplay" },
    { key: "/pronunciation", label: "Prononciation ciblée", intent: "pronunciation", icon: Mic, testid: "cmd-pronunciation" },
    { key: "/test", label: "Test de niveau adaptatif", intent: "test", icon: Sparkles, testid: "cmd-test" },
    { key: "/progress", label: "Tableau de bord et progression", intent: "progress", icon: LayoutDashboard, testid: "cmd-progress" },
    { key: "/translate", label: "Traducteur contextuel", intent: "translate", icon: Languages, testid: "cmd-translate" },
    { key: "/slow", label: "Activer / désactiver le rythme ralenti", intent: "slow", icon: Turtle, testid: "cmd-slow" },
    { key: "/challenge", label: "Défi pratique du jour", intent: "challenge", icon: Zap, testid: "cmd-challenge" },
    { key: "/podcast", label: "Podcasts Lexia", intent: "podcast", icon: HelpCircle, testid: "cmd-podcast" },
    { key: "/medical", label: "Parcours médical", intent: "medical", icon: HelpCircle, testid: "cmd-medical" },
];

export function useRunCommand() {
    const navigate = useNavigate();

    return async function run(intent) {
        switch (intent) {
            case "start":
                navigate("/onboarding");
                break;
            case "plan":
                navigate("/program");
                break;
            case "lesson":
                navigate("/lesson");
                break;
            case "review":
                navigate("/reviews");
                break;
            case "conversation":
                navigate("/conversation");
                break;
            case "pronunciation":
                navigate("/pronunciation");
                break;
            case "test": {
                try {
                    const { data } = await api.post("/placement/start");
                    if (data?.test_id) navigate(`/placement/${data.test_id}`);
                } catch {
                    toast.error("Impossible de démarrer le test.");
                }
                break;
            }
            case "progress":
                navigate("/dashboard");
                break;
            case "translate":
                navigate("/translate");
                break;
            case "slow": {
                try {
                    const prof = await api.get("/profile/me");
                    const enabled = !prof.data.profile?.slow_pace;
                    await api.post("/commands/slow", { enabled });
                    toast.success(enabled ? "Rythme ralenti activé." : "Rythme normal restauré.");
                } catch {
                    toast.error("Impossible de basculer le rythme.");
                }
                break;
            }
            case "challenge": {
                try {
                    const { data } = await api.post("/commands/challenge");
                    window.dispatchEvent(
                        new CustomEvent("lexi:challenge", { detail: data.challenge }),
                    );
                } catch {
                    toast.error("Impossible de générer le défi.");
                }
                break;
            }
            case "podcast":
                navigate("/podcasts");
                break;
            case "medical":
                navigate("/parcours/medical");
                break;
            case "podcast_soon":
                toast.info("Podcasts : disponibles en Phase 4.");
                break;
            case "medical_soon":
                toast.info("Spécialité médicale : disponible en Phase 5.");
                break;
            default:
                break;
        }
    };
}

export default function CommandPalette({ open, onClose }) {
    const [query, setQuery] = useState("");
    const [challenge, setChallenge] = useState(null);
    const inputRef = useRef(null);
    const run = useRunCommand();

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return COMMANDS;
        return COMMANDS.filter(
            (c) =>
                c.key.toLowerCase().includes(q) ||
                c.label.toLowerCase().includes(q),
        );
    }, [query]);

    useEffect(() => {
        if (open) {
            setQuery("");
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [open]);

    useEffect(() => {
        function onChallengeEvt(e) {
            setChallenge(e.detail);
        }
        window.addEventListener("lexi:challenge", onChallengeEvt);
        return () => window.removeEventListener("lexi:challenge", onChallengeEvt);
    }, []);

    // Global keyboard shortcut ⌘K / Ctrl+K to open the palette
    useEffect(() => {
        function onKey(e) {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent("lexi:palette:toggle"));
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    if (!open && !challenge) return null;

    return (
        <>
            {open && (
                <div
                    className="fixed inset-0 z-[100] bg-black/40 flex items-start justify-center pt-24 px-4"
                    onClick={onClose}
                    data-testid="command-palette-overlay"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-lexiBorder overflow-hidden"
                        data-testid="command-palette"
                    >
                        <div className="flex items-center gap-2 px-4 py-3 border-b border-lexiBorder">
                            <Search className="w-4 h-4 text-inkSoft" />
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Escape") onClose();
                                    if (e.key === "Enter" && filtered[0]) {
                                        onClose();
                                        run(filtered[0].intent);
                                    }
                                }}
                                placeholder="Cherchez une commande ou tapez /…"
                                data-testid="command-palette-input"
                                className="flex-1 bg-transparent focus:outline-none text-sm"
                            />
                            <span className="text-[10px] tracking-[0.22em] uppercase text-inkSoft">
                                ⌘K
                            </span>
                        </div>
                        <ul className="max-h-80 overflow-auto" data-testid="command-palette-list">
                            {filtered.length === 0 && (
                                <li className="px-4 py-6 text-sm text-inkSoft text-center">
                                    Aucune commande.
                                </li>
                            )}
                            {filtered.map((c) => (
                                <li key={c.key}>
                                    <button
                                        onClick={() => {
                                            onClose();
                                            run(c.intent);
                                        }}
                                        data-testid={c.testid}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-paperAlt"
                                    >
                                        <c.icon className="w-4 h-4 text-inkSoft" />
                                        <span className="font-mono text-xs text-terracotta w-24 shrink-0">
                                            {c.key}
                                        </span>
                                        <span className="text-sm">{c.label}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {challenge && (
                <div
                    className="fixed inset-0 z-[110] bg-black/40 flex items-center justify-center px-4"
                    onClick={() => setChallenge(null)}
                    data-testid="challenge-overlay"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-lexiBorder p-6"
                        data-testid="challenge-modal"
                    >
                        <p className="text-[11px] tracking-[0.28em] uppercase text-terracotta font-semibold mb-2">
                            Défi du jour
                        </p>
                        <h3 className="font-serif text-2xl leading-tight" data-testid="challenge-title">
                            {challenge.title_fr}
                        </h3>
                        <p className="mt-3 text-sm text-inkSoft" data-testid="challenge-body">
                            {challenge.challenge_fr}
                        </p>
                        <div className="mt-4 p-3 rounded-xl bg-paperAlt border border-lexiBorder">
                            <p className="font-serif italic" data-testid="challenge-sentence">
                                « {challenge.target_sentence} »
                            </p>
                            <p className="text-xs text-inkSoft mt-1">
                                {challenge.target_sentence_translation_fr}
                            </p>
                        </div>
                        <p className="mt-3 text-xs text-inkSoft">
                            {challenge.why_fr} · ~{challenge.duration_minutes} min
                        </p>
                        <div className="mt-5 flex justify-end">
                            <button
                                onClick={() => setChallenge(null)}
                                data-testid="challenge-close"
                                className="text-sm px-4 py-2 rounded-full bg-ink text-white hover:bg-emerald transition-colors"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
